// shared/domain/media.js  (reglas puras de imágenes de evidencia)

import { detectarFormatoImagen, esFormatoPermitido } from './formatoImagen';

/** Máximo del archivo elegido, ANTES de comprimir. */
export const MAX_IMAGE_INPUT_BYTES = 25 * 1024 * 1024;
/** Máximo de un archivo que se sube sin comprimir (solo si la compresión falló). */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Motivos de rechazo de una imagen al agregarla. */
export const MOTIVO_FORMATO = 'formato';
export const MOTIVO_TAMANO = 'tamano';
export const MOTIVO_PROCESAMIENTO = 'procesamiento';

/**
 * Valida una imagen por su CONTENIDO (bytes iniciales), no por extensión/MIME del navegador.
 * file: { name, size }. Devuelve { ok, reason: null | 'formato' | 'tamano', formato }.
 */
export const validarImagen = (file, bytes) => {
  const formato = detectarFormatoImagen(bytes);
  if (!esFormatoPermitido(formato)) return { ok: false, reason: MOTIVO_FORMATO, formato };
  if (file.size > MAX_IMAGE_INPUT_BYTES) return { ok: false, reason: MOTIVO_TAMANO, formato };
  return { ok: true, reason: null, formato };
};

const comillas = nombres => nombres.map(n => `«${n}»`).join(', ');

/**
 * Mensajes en español (uno por párrafo) para una lista de rechazados [{ name, reason }].
 * Formato inválido y tamaño se combinan en un solo mensaje de dos párrafos.
 */
export const mensajesImagenesRechazadas = rechazadas => {
  const formato = rechazadas.filter(r => r.reason === MOTIVO_FORMATO).map(r => r.name);
  const tamano = rechazadas.filter(r => r.reason === MOTIVO_TAMANO).map(r => r.name);
  const procesamiento = rechazadas.filter(r => r.reason === MOTIVO_PROCESAMIENTO).map(r => r.name);
  const mensajes = [];
  if (formato.length === 1) {
    mensajes.push(`El formato de la imagen «${formato[0]}» es inválido. Solo se permiten imágenes JPG o PNG.`);
  } else if (formato.length > 1) {
    mensajes.push(`El formato de las siguientes imágenes es inválido: ${comillas(formato)}. Solo se permiten imágenes JPG o PNG.`);
  }
  if (tamano.length === 1) {
    mensajes.push(`"${tamano[0]}": supera el tamaño máximo de 25 MB.`);
  } else if (tamano.length > 1) {
    mensajes.push(`Las siguientes imágenes superan el tamaño máximo de 25 MB: ${comillas(tamano)}.`);
  }
  procesamiento.forEach(nombre => mensajes.push(`No se pudo procesar la imagen «${nombre}». Intenta con otra foto.`));
  return mensajes;
};

/** URL pública de una imagen guardada; '' si no hay archivo. `baseUrl` viene de env.assetURL (ui). */
export const mediaUrl = (baseUrl, urlFile) => {
  if (!urlFile) return '';
  if (/^https?:\/\//i.test(urlFile)) return urlFile;
  return `${String(baseUrl || '').replace(/\/+$/, '')}/${String(urlFile).replace(/^\/+/, '')}`;
};

export const MSG_ERROR_SUBIDA = 'No se pudo subir la imagen.';

/** Mensaje en español de un fallo de subida: solo se muestra el del backend si viene de una respuesta controlada. */
export const mensajeErrorSubida = error =>
  error && error.name === 'DomainError' && error.controlled && error.message ? error.message : MSG_ERROR_SUBIDA;

export const MSG_ERROR_ELIMINACION = 'No se pudo eliminar la imagen.';

/** Mensaje en español de un fallo al eliminar: solo se muestra el del backend si viene de una respuesta controlada. */
export const mensajeErrorEliminacion = error =>
  error && error.name === 'DomainError' && error.controlled && error.message ? error.message : MSG_ERROR_ELIMINACION;

/** Cantidad con sustantivo: "1 imagen" / "3 imágenes". */
const imagenes = n => (n === 1 ? '1 imagen' : `${n} imágenes`);

/** Qué dispara la eliminación de las imágenes marcadas: el guardado del formulario o el botón "Eliminar" del detalle. */
export const DISPARADOR_GUARDAR = 'al guardar';
export const DISPARADOR_ELIMINAR = 'al presionar «Eliminar»';

/** Línea de sección con las imágenes guardadas marcadas para eliminar: "" si n <= 0. */
export const textoPendientesEliminar = (n, disparador = DISPARADOR_GUARDAR) => {
  if (!n || n < 1) return '';
  return n === 1 ? `1 imagen se eliminará ${disparador}` : `${n} imágenes se eliminarán ${disparador}`;
};

/** Texto (tooltip/aria-label) del botón "Deshacer" de una imagen marcada; con `mensaje` (fallo previo) lo antepone y habla de reintento. */
export const tituloDeshacerEliminar = (mensaje, disparador = DISPARADOR_GUARDAR) =>
  mensaje ? `${mensaje} Se reintentará ${disparador} · Deshacer` : `Se eliminará ${disparador} · Deshacer`;

/** Etiqueta del botón que aplica las eliminaciones del detalle: "Eliminar 1 imagen" / "Eliminar N imágenes". */
export const textoBotonEliminar = n => `Eliminar ${imagenes(n)}`;

/** Éxito al aplicar las eliminaciones: "1 imagen eliminada" / "N imágenes eliminadas". */
export const textoImagenesEliminadas = n => (n === 1 ? '1 imagen eliminada' : `${n} imágenes eliminadas`);

/** Aviso cuando algunas eliminaciones del detalle fallaron (siguen marcadas). */
export const mensajeEliminacionFallida = n =>
  `${imagenes(n)} no se ${n === 1 ? 'pudo' : 'pudieron'} eliminar. Presiona «Eliminar» para reintentar.`;

/** Título del modal al salir de la página con imágenes marcadas que aún no se eliminaron. */
export const TITULO_SALIDA_CON_MARCAS = '¿Confirmas que quieres salir de esta ventana?';

/** Descripción del modal de salida (sin la pregunta: el título ya la hace). */
export const descripcionSalidaConMarcas = n =>
  `Tienes ${imagenes(n)} ${n === 1 ? 'marcada' : 'marcadas'} para eliminar. Si sales, no se ${n === 1 ? 'eliminará' : 'eliminarán'}.`;

/** Aviso cuando algunas subidas del detalle fallaron (cada una tiene su botón de reintento). */
export const mensajeSubidasFallidas = n =>
  `${imagenes(n)} no se ${n === 1 ? 'pudo' : 'pudieron'} subir. Usa el botón de reintentar en cada imagen.`;

/** Descripción del modal de salida cuando aún hay imágenes subiéndose. */
export const descripcionSalidaConSubidas = n =>
  `Se ${n === 1 ? 'está subiendo' : 'están subiendo'} ${imagenes(n)}. Si sales, ${
    n === 1 ? 'no se completará' : 'no se completarán'
  } las subidas pendientes.`;

/** DELETE de una imagen que ya no existe (404): el resultado buscado ya se cumple, se trata como éxito. */
export const esImagenYaEliminada = error => !!error && error.status === 404;

/** Lista sin las imágenes con esos uuids (no muta). */
export const sinImagenes = (lista, uuids) => (lista || []).filter(media => !uuids.includes(media.uuid));

/** Copia de la lista con `cambios` aplicados a la imagen `uuid` (no muta). */
export const conCambiosEnImagen = (lista, uuid, cambios) =>
  (lista || []).map(media => (media.uuid === uuid ? { ...media, ...cambios } : media));

/** Marca la imagen para eliminarla (estado 'marked') o, con `eliminacion = null`, deshace la marca. */
export const conMarcaEliminacion = (lista, uuid, eliminacion = { estado: 'marked', error: null }) =>
  conCambiosEnImagen(lista, uuid, { eliminacion });

/** Imágenes que tienen marca de eliminación (marked | deleting | error). */
export const uuidsConMarca = lista => (lista || []).filter(media => media.eliminacion).map(media => media.uuid);

/** Imágenes que se eliminarán en la próxima aplicación (marked | error; no las que ya se están borrando). */
export const uuidsPorEliminar = lista =>
  (lista || []).filter(media => media.eliminacion && media.eliminacion.estado !== 'deleting').map(media => media.uuid);

/** Aplica el progreso de un DELETE: 'deleting' | 'error' (payload = mensaje) actualizan la marca; 'done' saca la imagen de la lista. */
export const conProgresoEliminacion = (lista, uuid, estado, payload) =>
  estado === 'done'
    ? sinImagenes(lista, [uuid])
    : conCambiosEnImagen(lista, uuid, {
        eliminacion: { estado: estado === 'error' ? 'error' : 'deleting', error: estado === 'error' ? payload || null : null },
      });

/**
 * Resumen cuando el padre se guardó pero algunas eliminaciones/subidas de imágenes fallaron.
 * `Entidad`: "La capacitación" | "La visita". Ej.: "La visita se guardó, pero 1 imagen no se pudo eliminar y 2 no se pudieron subir. Vuelve a guardar para reintentar."
 */
export const mensajeResultadoParcial = (Entidad, { eliminar = 0, subir = 0 } = {}) => {
  const partes = [];
  if (eliminar) partes.push(`${imagenes(eliminar)} no se ${eliminar === 1 ? 'pudo' : 'pudieron'} eliminar`);
  if (subir) partes.push(`${partes.length ? subir : imagenes(subir)} no se ${subir === 1 ? 'pudo' : 'pudieron'} subir`);
  return `${Entidad} se guardó, pero ${partes.join(' y ')}. Vuelve a guardar para reintentar.`;
};

/** Aviso si se intenta guardar un formulario mientras aún se optimizan imágenes. */
export const MSG_ESPERAR_OPTIMIZACION = 'Espera un momento: aún se están optimizando las imágenes.';

/** Máximo de imágenes por capacitación / visita (regla de producto; el backend aún no la valida). */
export const MAX_IMAGENES = 6;
/** Mensaje calmado al llegar al límite (dropzone y tooltip del botón deshabilitado). */
export const MSG_LIMITE_ALCANZADO = `Llegaste a las ${MAX_IMAGENES} imágenes. Si quieres cambiar alguna, quita una primero.`;

/** Título de la sección con contador: "Imágenes (3 de 6)". */
export const tituloImagenes = total => `Imágenes (${total || 0} de ${MAX_IMAGENES})`;

/** Cuántas imágenes más se pueden agregar dado el total actual (nunca negativo). */
export const espacioDisponible = totalActual => Math.max(0, MAX_IMAGENES - (totalActual || 0));

/** Conserva el orden: los primeros que caben son `aceptados`, el resto `excedentes`. */
export const recortarAlLimite = (validos, totalActual) => {
  const lista = Array.from(validos || []);
  const cupo = espacioDisponible(totalActual);
  return { aceptados: lista.slice(0, cupo), excedentes: lista.slice(cupo) };
};

/** Mensaje cuando se recortó por el límite; '' si no hubo excedentes. `agregadas` = las que sí entraron, `excedentes` = las que no (cada una número o arreglo de archivos). */
export const mensajeLimiteImagenes = ({ agregadas: agregadasIn = 0, excedentes: excedentesIn = 0 } = {}) => {
  const contar = v => (Array.isArray(v) ? v.length : Number(v) || 0);
  const agregadas = contar(agregadasIn);
  const excedentes = contar(excedentesIn);
  if (!excedentes) return '';
  if (!agregadas) return `Ya tienes ${MAX_IMAGENES} imágenes, que es el máximo. Si quieres cambiar alguna, quita una primero.`;
  const se = agregadas === 1 ? 'Se agregó 1 imagen' : `Se agregaron ${agregadas} imágenes`;
  const no = excedentes === 1 ? 'la otra no se incluyó' : `las otras ${excedentes} no se incluyeron`;
  return `${se}. Como el máximo es ${MAX_IMAGENES}, ${no}.`;
};

/** true si hay mensajes y ninguno es rechazo de formato/tamaño (solo se recortó por el límite). */
export const soloExcedeLimite = ({ rechazadas = [], agregadas = 0, excedentes = 0 } = {}) =>
  !(rechazadas || []).length && !!mensajeLimiteImagenes({ agregadas, excedentes });

/** Mensajes combinados para un solo Swal: rechazos de formato/tamaño primero, luego el del límite. */
export const mensajesAgregarImagenes = ({ rechazadas = [], agregadas = 0, excedentes = 0 } = {}) => {
  const limite = mensajeLimiteImagenes({ agregadas, excedentes });
  return [...mensajesImagenesRechazadas(rechazadas), ...(limite ? [limite] : [])];
};
